import { useClerk, useSession } from "@clerk/clerk-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { orpc, rpcClient } from "@/lib/rpc";

type LoginStep = "email" | "code";

function getRedirectUrl(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get("redirect") || "/";
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"
        fill="#EA4335"
      />
    </svg>
  );
}

export default function Login() {
  const [step, setStep] = useState<LoginStep>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [isClerkLoading, setIsClerkLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { client, loaded: clerkLoaded } = useClerk();
  const { session: clerkSession } = useSession();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();

  // Redirect to home if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      setLocation(getRedirectUrl());
    }
  }, [authLoading, isAuthenticated, setLocation]);

  // Bridge: when returning from Clerk OAuth with ?clerk=ready
  const clerkBridgeMutation = useMutation({
    mutationFn: async (sessionToken: string) => {
      return rpcClient.auth.clerkCallback({ sessionToken });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(orpc.auth.me.queryOptions().queryKey, data as any);
      toast({
        title: "Login realizado com sucesso",
        description: `Bem-vindo, ${data.employee?.fullName || data.user.email}!`,
      });
      window.location.href = data.user.isExternal ? "/jobs" : getRedirectUrl();
    },
    onError: (error: Error) => {
      setIsClerkLoading(false);
      toast({
        title: "Erro no login com Google",
        description: error.message || "Falha na autenticação",
        variant: "destructive",
      });
    },
  });

  // Detect Clerk session ready after OAuth redirect
  const bridgeMutate = clerkBridgeMutation.mutate;
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("clerk") === "ready" && clerkSession && !isClerkLoading) {
      setIsClerkLoading(true);
      clerkSession.getToken().then((token) => {
        if (token) {
          bridgeMutate(token);
        } else {
          setIsClerkLoading(false);
        }
      });
    }
  }, [clerkSession, bridgeMutate, isClerkLoading]);

  // Google OAuth via Clerk
  async function handleGoogleSignIn() {
    if (!clerkLoaded) return;
    try {
      await client.signIn.authenticateWithRedirect({
        strategy: "oauth_google",
        redirectUrl: `${window.location.origin}/sso-callback`,
        redirectUrlComplete: `${window.location.origin}/login?clerk=ready`,
      });
    } catch {
      toast({
        title: "Erro ao iniciar login com Google",
        description: "Tente novamente.",
        variant: "destructive",
      });
    }
  }

  // OTP mutations
  const requestOtpMutation = useMutation({
    ...orpc.otp.request.mutationOptions(),
    onSuccess: () => {
      setStep("code");
      toast({
        title: "Código enviado",
        description: "Verifique seu email para o código de verificação.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao enviar código",
        description: error.message || "Email não encontrado",
        variant: "destructive",
      });
    },
  });

  const verifyOtpMutation = useMutation({
    ...orpc.otp.verify.mutationOptions(),
    onSuccess: (data) => {
      queryClient.setQueryData(orpc.auth.me.queryOptions().queryKey, data as any);
      toast({
        title: "Login realizado com sucesso",
        description: `Bem-vindo, ${data.employee?.fullName || data.user.email}!`,
      });
      window.location.href = data.user.isExternal ? "/jobs" : getRedirectUrl();
    },
    onError: (error: Error) => {
      toast({
        title: "Erro no login",
        description: error.message || "Código inválido ou expirado",
        variant: "destructive",
      });
    },
  });

  const handleRequestOtp = (e: React.FormEvent) => {
    e.preventDefault();
    requestOtpMutation.mutate({ email });
  };

  const handleVerifyOtp = (e: React.FormEvent) => {
    e.preventDefault();
    verifyOtpMutation.mutate({ email, code });
  };

  const handleBack = () => {
    setStep("email");
    setCode("");
  };

  if (isClerkLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-rose-950 via-slate-950 to-slate-900">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-white/80" />
          <p className="text-white/60 text-sm">Autenticando com Google...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-[45%] flex-col justify-between bg-gradient-to-br from-rose-950 via-slate-950 to-slate-900 px-12 py-10 text-white relative overflow-hidden">
        {/* Subtle grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        {/* Logo */}
        <div className="relative z-10">
          <img src="/logo-comp.png" alt="Comp" className="h-8 w-auto" />
        </div>

        {/* Tagline */}
        <div className="relative z-10">
          <h1 className="text-4xl font-bold leading-tight tracking-tight">Best-in-class HR</h1>
          <p className="mt-4 text-lg text-white/50 max-w-md">Pessoas no centro, sempre</p>
        </div>

        {/* Footer */}
        <p className="relative z-10 text-sm text-white/30">
          &copy; {new Date().getFullYear()} Comp
        </p>
      </div>

      {/* Right sign-in panel */}
      <div className="flex flex-1 flex-col items-center justify-center px-6 sm:px-8 bg-background">
        <div className="w-full max-w-sm space-y-8">
          {/* Mobile logo */}
          <div>
            <div className="mb-3 lg:hidden">
              <img src="/logo-comp.png" alt="Comp" className="h-6 w-auto" />
            </div>

            <h2 className="text-2xl font-bold tracking-tight">
              {step === "email" ? "Entrar" : "Verificação"}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {step === "email"
                ? "Acesse sua conta para continuar"
                : `Código enviado para ${email}`}
            </p>
          </div>

          {step === "email" ? (
            <div className="space-y-6">
              {/* Google OAuth */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={!clerkLoaded}
                className="flex w-full items-center justify-center gap-3 rounded-lg border border-border bg-background px-4 py-3 text-sm font-medium shadow-sm transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="button-google-signin"
              >
                <GoogleIcon />
                Continuar com Google
              </button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">ou</span>
                </div>
              </div>

              {/* OTP Form */}
              <form onSubmit={handleRequestOtp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email corporativo
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@empresa.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11"
                    data-testid="input-email"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11"
                  disabled={requestOtpMutation.isPending}
                  data-testid="button-request-otp"
                >
                  {requestOtpMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="mr-2 h-4 w-4" />
                  )}
                  {requestOtpMutation.isPending ? "Enviando..." : "Receber código por email"}
                </Button>
              </form>
            </div>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="code" className="text-sm font-medium">
                  Código de verificação
                </Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  required
                  className="h-14 text-center text-2xl tracking-[0.3em] font-mono"
                  data-testid="input-code"
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11"
                disabled={verifyOtpMutation.isPending || code.length !== 6}
                data-testid="button-verify-otp"
              >
                {verifyOtpMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  "Entrar"
                )}
              </Button>
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="button-back"
                >
                  <ArrowLeft className="mr-1 h-3.5 w-3.5" />
                  Voltar
                </button>
                <button
                  type="button"
                  onClick={() => requestOtpMutation.mutate({ email })}
                  disabled={requestOtpMutation.isPending}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                  data-testid="button-resend-code"
                >
                  Reenviar código
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
